/**
 * OBJĘTOŚĆ MIĘŚNIOWA — KANON view 07's lower half: the two silhouettes, the four cluster columns,
 * and the weekly set count beside every bar.
 *
 * The ZONE is never printed as a word. It is a fill on the bar and on the muscle, and it reaches
 * anyone not looking at the screen through the row's own sentence (`ariaLabel`) — which is WCAG
 * 1.4.1 rather than a nicety, because a red-to-green ramp is meaning carried in colour alone.
 *
 * ── The thresholds are the CLIENT's, and the canon states them ────────────────────────────────
 * `GET /customer/{id}/muscle-volume` publishes **no zones**. It sends `weeklySets`, a flat
 * `weeklyTargetSets` of 15 and an uncapped `pctOfTarget`; MEV / MAV / MRV appear in the DTO's
 * XML-doc prose describing what the *frontend* plots against, and nowhere in the response.
 *
 * The canon prints the bands in its own legend — `0 · 1–9 · 10–20 · 21+` — and mobile's
 * `volumeZone` cuts at exactly those numbers. So the cuts below are the canon's, they match mobile
 * one for one, and they are named constants rather than a transcribed colour.
 *
 * ── Scored on WEEKLY sets, never on the window total ──────────────────────────────────────────
 * MEV/MAV/MRV are weekly bands. Mobile scored the window total once and over-counted every range
 * longer than a week; that was fixed there and is not repeated here. `weeklySets` is what the
 * figure, the bar, the printed count and the row's sentence all read.
 *
 * ── The API's own cluster is the grouping ─────────────────────────────────────────────────────
 * `cluster` arrives on every group as `Push | Pull | Legs | Core | Other`. Grouping by a table of
 * our own would be a second opinion about anatomy that the backend already has one about, and it
 * would silently misfile whatever muscle group the backend adds next.
 */

import type { BodySlug } from '~/shared/ui/bodyModel/bodyArtwork';
// The group → slug mapping moved to `shared/ui/bodyModel/regions.ts` on 3 September 2026, on
// its second consumer: the plan library's inspector fills the same figure from a plan's muscle
// split. It is a fact about the ARTWORK, and it changes when the artwork does.
import { REGIONS } from '~/shared/ui/bodyModel/regions';

/** The four zones, red-to-green, in the canon's own order. */
export type VolumeZone = 'none' | 'low' | 'opt' | 'high';

/** The canon's legend, verbatim: 0 · 1–9 · 10–20 · 21+. */
const LOW_FROM_SETS = 1;
const OPT_FROM_SETS = 10;
const HIGH_FROM_SETS = 21;

/**
 * The bar track's full width, in sets.
 *
 * **28, which is KANON klienci v2's own `scale`** — it was 24 (v1's, and mobile's) until the 31
 * August recalibration. The number is a drawing decision rather than a domain one: it is where the
 * track ends, so it decides where the MEV and MRV rules land on it (10/28 and 21/28) and how full a
 * given bar looks. Nothing scores against it.
 */
export const VOLUME_TRACK_MAX_SETS = 28;

/**
 * The set range each zone's legend entry opens with — KANON's own four tokens.
 *
 * They are not copy and are not translated: `10–20` reads the same in every language, and the
 * locale strings below already begin with them.
 */
const ZONE_RANGE: Readonly<Record<VolumeZone, string>> = {
  none: '0',
  low: '1–9',
  opt: '10–20',
  high: '21+',
};

export interface ZoneLegendEntry {
  /** The set range, which the canon sets in the card's own ink at 800. */
  readonly range: string;
  /** What that range means, in the ink and weight of ordinary prose. */
  readonly rest: string;
}

/**
 * One legend line, split into the figure and the sentence — KANON draws the range bold and the
 * explanation beside it in body weight.
 *
 * ── Split rather than stored twice, and that is the point ─────────────────────────────────────
 * The four strings are already in `src/shared/locales/*.json`, character for character as the canon
 * writes them, and that file is the mobile app's copy: it is never edited here. Minting a second
 * pair of keys in the `web:` namespace to get a bold prefix would put the same sentence in two
 * files and let them drift, which is the failure this app has a rule against.
 *
 * So the range comes from the constant above and the sentence is what the locale has after it. A
 * string that does NOT open with its range — a re-sync that rewords the line, a language that puts
 * the number last — falls back to printing the whole thing unemphasised rather than slicing a word
 * in half. That fallback is the reason this is a function in `model/` with a test rather than a
 * `slice` in the JSX.
 */
export function splitZoneLegend(zone: VolumeZone, text: string): ZoneLegendEntry {
  const range = ZONE_RANGE[zone];
  if (!text.startsWith(range)) return { range: '', rest: text };
  return { range, rest: text.slice(range.length) };
}

export function volumeZone(weeklySets: number): VolumeZone {
  if (weeklySets < LOW_FROM_SETS) return 'none';
  if (weeklySets < OPT_FROM_SETS) return 'low';
  if (weeklySets < HIGH_FROM_SETS) return 'opt';
  return 'high';
}

export interface MuscleGroupSource {
  /** The contract's PascalCase group name — `Chest`, `Quads`, `Other`… */
  readonly muscleGroup: string;
  readonly cluster: string;
  readonly weeklySets: number;
}

export interface MuscleRow {
  readonly key: string;
  readonly label: string;
  readonly weeklySets: number;
  readonly zone: VolumeZone;
  /** 0–100, the bar's fill against `VOLUME_TRACK_MAX_SETS`. */
  readonly fillPct: number;
  /** Where the two zone boundaries sit on the same track, so the bar can rule them. */
  readonly mevPct: number;
  readonly mrvPct: number;
  /** The row's whole meaning in one sentence — the zone is a colour ramp and needs words. */
  readonly ariaLabel: string;
}

export interface MuscleCluster {
  readonly key: 'Push' | 'Pull' | 'Legs' | 'Core';
  readonly label: string;
  readonly rows: readonly MuscleRow[];
}

/** One region of the silhouette: which shape to paint, and what it is worth. */
export interface SilhouetteRegion {
  /** The artwork's own slug. Typed, so a mapping typo is a build error and not a grey muscle. */
  readonly muscle: BodySlug;
  readonly zone: VolumeZone;
}

export interface MuscleVolumeView {
  readonly clusters: readonly MuscleCluster[];
  readonly front: readonly SilhouetteRegion[];
  readonly back: readonly SilhouetteRegion[];
  /**
   * Sets the silhouette cannot place. `0` hides the line.
   *
   * Every group with no entry in `REGIONS`, which is NOT the same as the contract's `Other`
   * CLUSTER — the comment here used to say cluster and the code has always filtered on the group.
   * They usually coincide, and would stop the moment the backend added a muscle group this build's
   * mapping does not know: it would land in `Push` or `Pull` with a real cluster and still have
   * nowhere to be drawn. Counting by group is what keeps the figure honest in that case, which is
   * the case it exists for.
   */
  readonly otherSets: number;
}

const CLUSTER_ORDER = ['Push', 'Pull', 'Legs', 'Core'] as const;


/**
 * A cluster's name.
 *
 * Three of the four are English words in both languages — `Push`, `Pull`, `Core` — and the copied
 * locale prints them as such. The fourth is not: `clients.detail.progress.muscle.clusters.legs`
 * still reads "Legs" in the Polish file, so a Polish trainer saw three English section names and one
 * English word that has a perfectly ordinary Polish equivalent. KANON writes "Nogi".
 *
 * `src/shared/locales/{pl,en}.json` are the mobile app's copy and are never edited here — a re-sync
 * overwrites them — so the value is overridden from the `web:` namespace instead, exactly as
 * `clientDetailTabLabel` overrides the Trendy tab's own label and for the same reason. Delete the
 * override, not the key, when the copy catches up.
 */
function clusterLabel(
  key: (typeof CLUSTER_ORDER)[number],
  t: MuscleVolumeDeps['t'],
): string {
  if (key === 'Legs') return t('web:clientDetail.volume.clusterLegs');
  return t(`clients.detail.progress.muscle.clusters.${key.toLowerCase()}`);
}

export interface MuscleVolumeDeps {
  readonly t: (key: string, options?: Record<string, unknown>) => string;
}

function pctOf(sets: number): number {
  return Math.min(100, Math.round((sets / VOLUME_TRACK_MAX_SETS) * 100));
}

export function buildMuscleVolume(
  groups: readonly MuscleGroupSource[] | null,
  deps: MuscleVolumeDeps,
): MuscleVolumeView {
  const { t } = deps;
  const source = groups ?? [];

  const clusters: MuscleCluster[] = CLUSTER_ORDER.flatMap((key) => {
    const members = source.filter((group) => group.cluster === key);
    if (members.length === 0) return [];
    return [
      {
        key,
        label: clusterLabel(key, t),
        rows: members.map((group) => {
          const zone = volumeZone(group.weeklySets);
          // The backend's English group name is the fallback, exactly as mobile does it: a group
          // this build has no Polish for still names itself rather than rendering a raw key.
          const label = t(`clients.detail.progress.muscle.groups.${group.muscleGroup}`, {
            defaultValue: group.muscleGroup,
          });
          return {
            key: group.muscleGroup,
            label,
            weeklySets: group.weeklySets,
            zone,
            fillPct: pctOf(group.weeklySets),
            mevPct: pctOf(OPT_FROM_SETS),
            mrvPct: pctOf(HIGH_FROM_SETS),
            // WCAG 1.4.1: the zone is a red-to-green ramp, so the sentence carries it too. Mobile
            // uses the same template.
            ariaLabel: t('clients.detail.progress.muscle.rowA11y', {
              name: label,
              sets: group.weeklySets,
              zone: t(`clients.detail.progress.muscle.zone.${zone}`),
            }),
          };
        }),
      },
    ];
  });

  /**
   * The regions one contract group paints, or none.
   *
   * `Object.hasOwn` rather than a bare index, and this is the site where it MATTERS rather than
   * merely tidies: a wire value of `constructor` resolves through `Object.prototype` to a function,
   * which is not nullish, so `?? []` passes it straight through and `.filter` throws — taking the
   * whole Trendy tab down. Unreachable today (the contract emits C# enum names) and cheap to close.
   */
  const regionsOf = (muscleGroup: string) =>
    Object.hasOwn(REGIONS, muscleGroup) ? (REGIONS[muscleGroup] ?? []) : [];

  const regionsFor = (side: 'front' | 'back'): SilhouetteRegion[] =>
    source.flatMap((group) =>
      regionsOf(group.muscleGroup)
        .filter((region) => region.side === side)
        .map((region) => ({ muscle: region.muscle, zone: volumeZone(group.weeklySets) })),
    );

  return {
    clusters,
    front: regionsFor('front'),
    back: regionsFor('back'),
    otherSets: Math.round(
      source
        // `in` walks the prototype chain too, so a group called `toString` would be counted as
        // DRAWN and silently dropped from the "Inne" figure. Own keys only.
        .filter((group) => !Object.hasOwn(REGIONS, group.muscleGroup))
        .reduce((sum, group) => sum + group.weeklySets, 0),
    ),
  };
}

/**
 * Which silhouette this client is drawn as.
 *
 * **Mobile's rule, ported**: `useResolvedGender` reads the profile's sex and answers `'female'` for
 * a woman and `'male'` for everything else — the figure has two drawings and the contract has three
 * values plus `null`, so `Other` and an unread sex fall to the same default the phone uses. What is
 * NOT done here is guessing from a name or a measurement; a client whose sex the profile does not
 * carry is drawn as the default rather than as a guess about them.
 *
 * The value arrives as the contract's own string (`Male | Female | Other`) rather than as an enum
 * import, because `model/` states its inputs and does not reach into `api/`.
 */
export function silhouetteGender(gender: string | null | undefined): 'male' | 'female' {
  return gender === 'Female' ? 'female' : 'male';
}
